import { siteNavigationConfig } from "../config/siteNavigation"
import type { NavigationConfigItem, NavigationFeatureFlag } from "../types"

export type { NavigationFeatureFlag } from "../types"

export interface NavigationCommandNode {
  baseHref?: string
  children?: NavigationCommandNode[]
  featureFlag?: NavigationFeatureFlag
  id: string
  key?: string
  keywords: string[]
  label: string
  parentId: string | null
  requiresAdmin?: boolean
}

export interface FlattenedNavigationCommandNode extends NavigationCommandNode {
  depth: number
  description?: string
  isBranch: boolean
  pathLabels: string[]
}

export const DIRECT_SHORTCUTS_BY_BASE_HREF: Record<string, string> = {
  "/admin": "g z",
  "/agents": "g a",
  "/benchmarks/stt": "g 1",
  "/benchmarks/tts": "g 2",
  "/dashboard": "g d",
  "/home": "g h",
  "/home/investigations": "g f",
  "/home/notebooks": "g b",
  "/metrics": "g m",
  "/uploaded": "g o",
  "/alerts": "g i",
  "/personas": "g p",
  "/reports": "g e",
  "/review": "g v",
  "/review/projects": "g v",
  "/simulated": "g r",
  "/scheduled": "g s",
  "/settings": "g x",
  "/simulate": "g n",
  "/templates": "g l",
  "/test-sets": "g t",
  "/traces": "g c",
}

const createNodeId = (label: string, baseHref?: string) =>
  baseHref
    ? `nav${baseHref}`
    : `section-${label.toLowerCase().replace(/\s+/g, "-")}`

function buildSiteConfigNodes(
  items: NavigationConfigItem[],
  parentId: string | null = null,
): NavigationCommandNode[] {
  return items.map((item) => {
    const hasChildren = Array.isArray(item.subItems) && item.subItems.length > 0
    const nodeId = hasChildren
      ? createNodeId(item.label)
      : createNodeId(item.label, item.base_href)

    return {
      baseHref: hasChildren ? undefined : item.base_href,
      children: hasChildren
        ? item.subItems?.map((subItem) => ({
            baseHref: subItem.base_href,
            featureFlag:
              "featureFlag" in subItem ? subItem.featureFlag : undefined,
            id: createNodeId(subItem.label, subItem.base_href),
            key: DIRECT_SHORTCUTS_BY_BASE_HREF[subItem.base_href],
            keywords: subItem.keywords ?? [subItem.label.toLowerCase()],
            label: subItem.label,
            parentId: nodeId,
          }))
        : undefined,
      featureFlag: item.featureFlag,
      id: nodeId,
      key: hasChildren
        ? undefined
        : DIRECT_SHORTCUTS_BY_BASE_HREF[item.base_href],
      keywords: item.keywords ?? [item.label.toLowerCase()],
      label: item.label,
      parentId,
    }
  })
}

/**
 * Sofia's own pages. They hang under the Home entry as sidebar rows that Sofia
 * renders herself rather than as config, so nothing above puts them in this
 * tree — but they are destinations like any other, and a chord that reaches
 * every other sidebar row and not these two would be the odd one out.
 */
const SOFIA_HOME_DESTINATIONS: NavigationCommandNode[] = [
  {
    baseHref: "/home/investigations",
    id: createNodeId("Investigations", "/home/investigations"),
    key: DIRECT_SHORTCUTS_BY_BASE_HREF["/home/investigations"],
    keywords: ["investigations", "history", "sofia", "findings"],
    label: "Investigations",
    parentId: null,
  },
  {
    baseHref: "/home/notebooks",
    featureFlag: "sofia-notebooks",
    id: createNodeId("Notebooks", "/home/notebooks"),
    key: DIRECT_SHORTCUTS_BY_BASE_HREF["/home/notebooks"],
    keywords: ["notebooks", "sofia", "reports"],
    label: "Notebooks",
    parentId: null,
  },
]

/**
 * The palette's tree is the sidebar's, plus the entries that have no sidebar
 * row — Simulate, Settings, and Sofia's own pages.
 *
 * It takes the nav items rather than reading the config directly so the palette
 * follows whichever sidebar the viewer has: a `g` shortcut that walked a
 * section the viewer cannot see would be a path to nowhere.
 */
function buildNavigationCommandTree(
  navItems: NavigationConfigItem[],
): NavigationCommandNode[] {
  return [
    {
      baseHref: "/simulate",
      id: createNodeId("Simulate", "/simulate"),
      key: DIRECT_SHORTCUTS_BY_BASE_HREF["/simulate"],
      keywords: ["simulate", "new run", "launch simulation"],
      label: "Simulate",
      parentId: null,
    },
    ...buildSiteConfigNodes(navItems),
    ...SOFIA_HOME_DESTINATIONS,
    {
      baseHref: "/settings",
      id: createNodeId("Settings", "/settings"),
      key: DIRECT_SHORTCUTS_BY_BASE_HREF["/settings"],
      keywords: ["settings", "preferences", "integrations", "configuration"],
      label: "Settings",
      parentId: null,
    },
    {
      baseHref: "/admin",
      id: createNodeId("Admin", "/admin"),
      key: DIRECT_SHORTCUTS_BY_BASE_HREF["/admin"],
      keywords: ["admin"],
      label: "Admin",
      parentId: null,
      requiresAdmin: true,
    },
  ]
}

export const NAVIGATION_COMMAND_TREE: NavigationCommandNode[] =
  buildNavigationCommandTree(siteNavigationConfig.navItems)

export function flattenNavigationCommandTree(
  nodes: NavigationCommandNode[],
  parentLabels: string[] = [],
): FlattenedNavigationCommandNode[] {
  return nodes.flatMap((node) => {
    const pathLabels = [...parentLabels, node.label]
    const flattenedNode: FlattenedNavigationCommandNode = {
      ...node,
      depth: parentLabels.length,
      description: parentLabels[parentLabels.length - 1],
      isBranch: (node.children?.length ?? 0) > 0,
      pathLabels,
    }

    return [
      flattenedNode,
      ...(node.children
        ? flattenNavigationCommandTree(node.children, pathLabels)
        : []),
    ]
  })
}

export function filterNavigationCommandTree(
  nodes: NavigationCommandNode[],
  options: {
    canAccess: (baseHref: string) => boolean
    featureFlagStatus: Partial<Record<NavigationFeatureFlag, boolean>>
    hasAdminAccess: boolean
  },
): NavigationCommandNode[] {
  return nodes.flatMap((node) => {
    if (node.requiresAdmin && !options.hasAdminAccess) {
      return []
    }

    if (
      node.featureFlag &&
      options.featureFlagStatus[node.featureFlag] !== true
    ) {
      return []
    }

    const children = node.children
      ? filterNavigationCommandTree(node.children, options)
      : undefined

    if (node.baseHref && !options.canAccess(node.baseHref)) {
      return children?.length ? [{ ...node, children }] : []
    }

    if (!node.baseHref && children?.length === 0) {
      return []
    }

    return [{ ...node, children }]
  })
}
